const CART_ERROR = 'Impossible d’ajouter cette pièce au panier. Réessayez dans un instant.';

const parseVariants = (root) => {
  try {
    return JSON.parse(root.querySelector('[data-ql-variants-json]')?.textContent || '[]');
  } catch {
    return [];
  }
};

const addVariantToCart = async (variantId, quantity = 1) => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${rootPath}cart/add.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ id: Number(variantId), quantity }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || CART_ERROR);
  document.dispatchEvent(new CustomEvent('ql:cart-refresh', { detail: { open: true } }));
  return data;
};

const initializeGallery = (root) => {
  const gallery = root.querySelector('[data-ql-gallery]');
  const track = gallery?.querySelector('[data-ql-gallery-track]');
  const slides = [...(gallery?.querySelectorAll('[data-ql-gallery-slide]') || [])];
  const thumbs = [...(gallery?.querySelectorAll('[data-ql-gallery-thumb]') || [])];
  const dots = [...(gallery?.querySelectorAll('[data-ql-gallery-dots] i') || [])];
  const previous = gallery?.querySelector('[data-ql-gallery-prev]');
  const next = gallery?.querySelector('[data-ql-gallery-next]');
  if (!track || !slides.length) return () => {};

  let activeIndex = 0;
  let scrollFrame = 0;

  const lastStartIndex = () => {
    const slideWidth = slides[0]?.getBoundingClientRect().width || track.clientWidth;
    const visibleSlides = Math.max(1, Math.floor((track.clientWidth + 1) / Math.max(slideWidth, 1)));
    return Math.max(0, slides.length - visibleSlides);
  };

  const update = (index) => {
    activeIndex = Math.max(0, Math.min(index, slides.length - 1));
    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === activeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
    });
    dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
    if (previous) previous.disabled = activeIndex === 0;
    if (next) next.disabled = activeIndex >= lastStartIndex();
  };

  const goTo = (index, behavior = 'smooth') => {
    const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!target) return;
    track.scrollTo({ left: target.offsetLeft, behavior });
    update(Number(target.dataset.galleryIndex || index));
  };

  const updateFromScroll = () => {
    scrollFrame = 0;
    const target = slides.reduce((nearest, slide) => {
      const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
      return distance < nearest.distance ? { slide, distance } : nearest;
    }, { slide: slides[0], distance: Number.POSITIVE_INFINITY }).slide;
    update(Number(target.dataset.galleryIndex || 0));
  };

  track.addEventListener('scroll', () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(updateFromScroll);
  }, { passive: true });
  previous?.addEventListener('click', () => goTo(activeIndex - 1));
  next?.addEventListener('click', () => goTo(Math.min(activeIndex + 1, lastStartIndex())));
  thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryIndex || 0))));
  window.addEventListener('resize', () => update(activeIndex), { passive: true });
  update(0);

  return (mediaId) => {
    if (!mediaId) return;
    const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index >= 0) goTo(index);
  };
};

const initializeVariants = (root, selectGalleryMedia) => {
  const variants = parseVariants(root);
  const groups = [...root.querySelectorAll('[data-ql-option-group]')];
  const hiddenVariant = root.querySelector('[data-ql-variant-select]');
  const currentPrice = root.querySelector('[data-ql-product-current-price]');
  const comparePrice = root.querySelector('[data-ql-product-compare-price]');
  if (!variants.length || !hiddenVariant) return;

  const selectedOptions = groups.map((group) => {
    const selected = group.querySelector('[data-ql-option-value].is-selected');
    return selected?.dataset.qlOptionValue || '';
  });

  const matchesOptions = (variant, options) =>
    options.every((value, index) => !value || String(variant.options[index]) === String(value));

  const updateOptionAvailability = () => {
    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-ql-option-value]').forEach((button) => {
        const candidate = [...selectedOptions];
        candidate[groupIndex] = button.dataset.qlOptionValue || '';
        const available = variants.some((variant) => variant.available && matchesOptions(variant, candidate));
        button.classList.toggle('is-unavailable', !available);
        button.setAttribute('aria-disabled', String(!available));
      });
    });
  };

  const render = (changeUrl = true) => {
    const variant = variants.find((item) => matchesOptions(item, selectedOptions)) || null;
    hiddenVariant.value = variant?.id || '';
    hiddenVariant.dataset.title = variant?.title || '';
    hiddenVariant.dataset.available = String(Boolean(variant?.available));
    hiddenVariant.dataset.price = variant?.price || '';
    hiddenVariant.dispatchEvent(new Event('change', { bubbles: true }));

    if (currentPrice) currentPrice.textContent = variant?.price || '';
    if (comparePrice) {
      comparePrice.textContent = variant?.compareAtPrice || '';
      comparePrice.hidden = !variant?.compareAtPrice;
    }

    groups.forEach((group, groupIndex) => {
      const value = selectedOptions[groupIndex] || '';
      const current = group.querySelector('[data-ql-option-current]');
      if (current) current.textContent = value;
    });

    updateOptionAvailability();
    if (variant?.featuredMediaId) selectGalleryMedia(variant.featuredMediaId);
    if (variant?.id && changeUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  };

  groups.forEach((group) => {
    const groupIndex = Number(group.dataset.optionIndex || 0);
    group.querySelectorAll('[data-ql-option-value]').forEach((button) => {
      button.addEventListener('click', () => {
        selectedOptions[groupIndex] = button.dataset.qlOptionValue || '';
        group.querySelectorAll('[data-ql-option-value]').forEach((candidate) => {
          const selected = candidate === button;
          candidate.classList.toggle('is-selected', selected);
          candidate.setAttribute('aria-pressed', String(selected));
        });
        render();
      });
    });
  });

  render(false);
};

const initializeSizeGuide = (root) => {
  const modal = root.querySelector('[data-ql-size-guide]');
  const openButtons = root.querySelectorAll('[data-ql-open-size-guide]');
  const closeButtons = modal?.querySelectorAll('[data-ql-close-size-guide]') || [];
  const bust = modal?.querySelector('[data-ql-size-bust]');
  const waist = modal?.querySelector('[data-ql-size-waist]');
  const hips = modal?.querySelector('[data-ql-size-hips]');
  const findButton = modal?.querySelector('[data-ql-find-size]');
  const result = modal?.querySelector('[data-ql-size-result]');
  if (!modal) return;

  const sizes = [
    { letter: 'XS', number: 34, bust: 80, waist: 66, hips: 88 },
    { letter: 'S', number: 36, bust: 84, waist: 70, hips: 92 },
    { letter: 'M', number: 38, bust: 88, waist: 74, hips: 96 },
    { letter: 'L', number: 40, bust: 92, waist: 78, hips: 100 },
    { letter: 'XL', number: 42, bust: 98, waist: 84, hips: 106 },
    { letter: '2XL', number: 44, bust: 104, waist: 90, hips: 112 },
    { letter: '3XL', number: 46, bust: 110, waist: 96, hips: 118 },
  ];

  const setOpen = (open) => {
    modal.hidden = !open;
    document.documentElement.classList.toggle('ql-size-guide-open', open);
    if (open) window.setTimeout(() => bust?.focus(), 60);
  };

  openButtons.forEach((button) => button.addEventListener('click', () => setOpen(true)));
  closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) setOpen(false);
  });

  findButton?.addEventListener('click', () => {
    const measurements = {
      bust: Number.parseFloat(bust?.value || ''),
      waist: Number.parseFloat(waist?.value || ''),
      hips: Number.parseFloat(hips?.value || ''),
    };
    const hasMeasurement = Object.values(measurements).some(Number.isFinite);
    if (!hasMeasurement) {
      result.textContent = 'أدخلي قياسًا واحدًا على الأقل لنقترح عليك المقاس.';
      return;
    }

    const recommendation = sizes.find((size) =>
      (!Number.isFinite(measurements.bust) || measurements.bust <= size.bust) &&
      (!Number.isFinite(measurements.waist) || measurements.waist <= size.waist) &&
      (!Number.isFinite(measurements.hips) || measurements.hips <= size.hips)
    );

    result.textContent = recommendation
      ? `المقاس المقترح: ${recommendation.letter} (${recommendation.number}). سنؤكده معك هاتفيًا قبل الإرسال.`
      : 'قياساتك تتجاوز الجدول الحالي. تواصلي معنا وسنساعدك في اختيار المقاس الأنسب.';
  });
};

const initializeCompleteLook = (root) => {
  const trigger = root.querySelector('[data-ql-open-complete-look]');
  const section = root.querySelector('[data-ql-complete-look]');
  const track = section?.querySelector('[data-ql-look-track]');
  const cards = [...(section?.querySelectorAll('[data-ql-look-card]') || [])];
  const previous = section?.querySelector('[data-ql-look-prev]');
  const next = section?.querySelector('[data-ql-look-next]');
  const dots = section?.querySelector('[data-ql-look-dots]');
  if (!trigger || !section || !track || !cards.length) return;

  let activeIndex = 0;
  let scrollFrame = 0;

  const renderDots = () => {
    if (!dots) return;
    dots.innerHTML = cards.map((_, index) => `<button type="button" aria-label="Voir le produit ${index + 1}"${index === 0 ? ' class="is-active"' : ''}></button>`).join('');
    dots.querySelectorAll('button').forEach((button, index) => button.addEventListener('click', () => goTo(index)));
  };

  const update = (index) => {
    activeIndex = Math.max(0, Math.min(index, cards.length - 1));
    cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === activeIndex));
    dots?.querySelectorAll('button').forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
    if (previous) previous.disabled = activeIndex === 0;
    if (next) next.disabled = activeIndex === cards.length - 1;
  };

  const goTo = (index) => {
    const card = cards[Math.max(0, Math.min(index, cards.length - 1))];
    if (!card) return;
    const wrapper = card.parentElement !== track && card.parentElement?.parentElement === track ? card.parentElement : card;
    track.scrollTo({ left: wrapper.offsetLeft - Math.max(0, (track.clientWidth - wrapper.clientWidth) / 2), behavior: 'smooth' });
    update(index);
  };

  trigger.addEventListener('click', () => {
    const opening = section.hidden;
    if (!opening) {
      section.classList.remove('is-visible');
      window.setTimeout(() => { section.hidden = true; }, 260);
      trigger.setAttribute('aria-expanded', 'false');
      return;
    }

    section.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      section.classList.add('is-visible');
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      goTo(activeIndex);
    });
  });

  previous?.addEventListener('click', () => goTo(activeIndex - 1));
  next?.addEventListener('click', () => goTo(activeIndex + 1));
  track.addEventListener('scroll', () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      const nearest = cards.reduce((best, card, index) => {
        const wrapper = card.parentElement !== track && card.parentElement?.parentElement === track ? card.parentElement : card;
        const center = wrapper.offsetLeft + wrapper.clientWidth / 2;
        const distance = Math.abs(center - (track.scrollLeft + track.clientWidth / 2));
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Number.POSITIVE_INFINITY });
      update(nearest.index);
    });
  }, { passive: true });

  section.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-ql-look-add]');
    if (!button) return;
    const card = button.closest('[data-ql-look-card]');
    const variantSelector = card?.querySelector('[data-ql-look-variant]');
    const message = card?.querySelector('[data-ql-look-message]');
    const label = button.querySelector('[data-ql-look-add-label]');
    const variantId = variantSelector?.value || '';

    if (!variantId) {
      if (message) message.textContent = 'Choisissez d’abord une taille.';
      variantSelector?.focus();
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    if (message) message.textContent = '';
    if (label) label.textContent = 'Ajout en cours';
    try {
      await addVariantToCart(variantId, 1);
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      if (label) label.textContent = 'Ajouté au panier';
      if (message) message.textContent = 'La pièce a été ajoutée à votre sélection.';
      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
        if (label) label.textContent = button.dataset.defaultLabel || 'Ajouter au panier';
      }, 1700);
    } catch (error) {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (label) label.textContent = button.dataset.defaultLabel || 'Ajouter au panier';
      if (message) message.textContent = error.message || CART_ERROR;
    }
  });

  renderDots();
  update(0);
};

const initializeRecommendations = (root) => {
  const shell = root.querySelector('[data-ql-recommendations]');
  const track = shell?.querySelector('[data-ql-recommendations-track]');
  const previous = shell?.querySelector('[data-ql-recommendations-prev]');
  const next = shell?.querySelector('[data-ql-recommendations-next]');
  if (!track || !previous || !next) return;

  const step = () => {
    const card = track.querySelector('.ql-luxury-card');
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || '0');
    return (card?.getBoundingClientRect().width || track.clientWidth * 0.5) + gap;
  };
  const update = () => {
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth - 2);
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maximum;
  };
  previous.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
  next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  requestAnimationFrame(update);
};

const initializeConversionMarketing = (root) => {
  const variants = parseVariants(root);
  const hiddenVariant = root.querySelector('[data-ql-variant-select]');
  const stock = root.querySelector('[data-ql-stock]');
  const stockLabel = stock?.querySelector('[data-ql-stock-label]');
  const stockProgress = stock?.querySelector('[data-ql-stock-progress]');
  const mobilePrice = root.querySelector('[data-ql-mobile-price]');
  const mobileOrder = root.querySelector('[data-ql-mobile-order]');
  const countdown = root.querySelector('[data-ql-countdown]');
  const reviewBubble = root.querySelector('[data-ql-review-bubble]');

  const selectedVariant = () => variants.find((variant) => String(variant.id) === String(hiddenVariant?.value || '')) || null;

  const renderVariantMarketing = () => {
    const variant = selectedVariant();
    if (!variant) return;

    if (mobilePrice) mobilePrice.textContent = variant.price || '';
    if (!stock || !stockLabel || !stockProgress) return;

    const threshold = Math.max(1, Number(stock.dataset.threshold || 8));
    const quantity = Number(variant.inventoryQuantity || 0);
    const tracked = Boolean(variant.inventoryTracked);
    const low = tracked && quantity > 0 && quantity <= threshold;
    const comfortable = !low;

    stock.classList.toggle('is-comfortable', comfortable);
    if (low) {
      stockLabel.textContent = (stock.dataset.lowTemplate || 'باقي [count] قطع فقط').replace('[count]', String(quantity));
    } else if (tracked) {
      stockLabel.textContent = stock.dataset.readyText || 'متوفر الآن وجاهز لتأكيد الطلب';
    } else {
      stockLabel.textContent = stock.dataset.untrackedText || 'متوفر الآن — سنتصل بك للتأكيد';
    }

    const percent = tracked && quantity > 0 ? Math.max(8, Math.min(100, (quantity / threshold) * 100)) : 100;
    stockProgress.style.setProperty('--ql-stock-progress', `${percent}%`);
  };

  hiddenVariant?.addEventListener('change', renderVariantMarketing);
  renderVariantMarketing();

  mobileOrder?.addEventListener('click', () => {
    root.querySelector('[data-ql-open-order]')?.click();
  });

  if (reviewBubble) {
    try {
      const messages = JSON.parse(reviewBubble.querySelector('[data-ql-review-bubble-messages]')?.textContent || '[]')
        .filter((message) => message?.text);
      const text = reviewBubble.querySelector('[data-ql-review-bubble-text]');
      const language = reviewBubble.querySelector('[data-ql-review-bubble-language]');
      const interval = Math.max(3000, Number(reviewBubble.dataset.interval || 5000));
      let activeMessage = 0;

      if (messages.length > 1 && text && language) {
        const bubbleTimer = window.setInterval(() => {
          if (!root.isConnected) {
            window.clearInterval(bubbleTimer);
            return;
          }
          reviewBubble.classList.add('is-changing');
          window.setTimeout(() => {
            activeMessage = (activeMessage + 1) % messages.length;
            const message = messages[activeMessage];
            text.textContent = `“${message.text}”`;
            text.dir = message.direction || 'auto';
            language.textContent = message.language || '';
            reviewBubble.classList.remove('is-changing');
          }, 190);
        }, interval);
      }
    } catch {
      // Keep the server-rendered Arabic message if editor content is temporarily invalid.
    }
  }

  if (!countdown) return;
  const hours = countdown.querySelector('[data-ql-countdown-hours]');
  const minutes = countdown.querySelector('[data-ql-countdown-minutes]');
  const seconds = countdown.querySelector('[data-ql-countdown-seconds]');
  const mode = countdown.dataset.mode || 'daily';
  const duration = Math.max(5, Number(countdown.dataset.minutes || 15)) * 60 * 1000;
  const storageKey = `ql-pdp-offer:${root.dataset.productHandle || 'product'}`;
  let deadline;

  if (mode === 'session') {
    try {
      const stored = Number(sessionStorage.getItem(storageKey) || 0);
      deadline = stored > Date.now() ? stored : Date.now() + duration;
      sessionStorage.setItem(storageKey, String(deadline));
    } catch {
      deadline = Date.now() + duration;
    }
  } else {
    const endOfDay = new Date();
    endOfDay.setHours(24, 0, 0, 0);
    deadline = endOfDay.getTime();
  }

  const pad = (value) => String(value).padStart(2, '0');
  const renderCountdown = () => {
    if (!root.isConnected) return false;
    let remaining = Math.max(0, deadline - Date.now());
    if (remaining === 0 && mode === 'daily') {
      const nextDay = new Date();
      nextDay.setHours(24, 0, 0, 0);
      deadline = nextDay.getTime();
      remaining = Math.max(0, deadline - Date.now());
    }
    const totalSeconds = Math.floor(remaining / 1000);
    if (hours) hours.textContent = pad(Math.floor(totalSeconds / 3600));
    if (minutes) minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
    if (seconds) seconds.textContent = pad(totalSeconds % 60);
    return true;
  };

  renderCountdown();
  const timer = window.setInterval(() => {
    if (!renderCountdown()) window.clearInterval(timer);
  }, 1000);
};

const initializeProductPage = (root) => {
  if (root.dataset.qlProductInitialized === 'true') return;
  root.dataset.qlProductInitialized = 'true';
  const selectGalleryMedia = initializeGallery(root);
  initializeVariants(root, selectGalleryMedia);
  initializeSizeGuide(root);
  initializeCompleteLook(root);
  initializeRecommendations(root);
  initializeConversionMarketing(root);
};

const initialize = (scope = document) => {
  scope.querySelectorAll('[data-ql-product-page]').forEach(initializeProductPage);
};

document.addEventListener('shopify:section:load', (event) => initialize(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialize());
} else {
  initialize();
}
